/** Rules for the Print Labels "By Style" tab: what auto-selects, and what prints. */

/** Below this, the type-ahead stays quiet rather than querying on one character. */
export const MIN_SEARCH_LENGTH = 2;

/** Pause after the last keystroke before querying. */
export const SEARCH_DEBOUNCE_MS = 300;

export const SEARCH_TYPE_OPTIONS = [
    { label: 'Child SKU', value: 'childSku' },
    { label: 'Child UPC', value: 'childUpc' },
    { label: 'Vendor part number', value: 'vendorPartNumber' },
];

/**
 * Whether a result set should skip the list and load the variant table directly.
 *
 * Auto-selects when exactly one match is exact, no matter how many substring matches
 * come along with it. Substring matches are expected company for an exact one now that
 * all three search types match loosely: a short SKU like `69645` routinely turns up
 * inside longer SKUs from unrelated, high-variant styles, so requiring the whole result
 * set to contain a single match (the earlier rule) meant a complete, unambiguous SKU
 * failed to auto-select as soon as any other product happened to contain it as a
 * substring. Zero exact matches still means no auto-select — the mid-typing case
 * (`1001` on the way to `10015160`) has no exact match yet, so nothing hijacks the
 * field — and two exact matches (the same SKU shared by two masters) still means no
 * auto-select, since only the operator can tell those apart. Only the server knows
 * what a match matched on, so exactness comes from the `exactMatch` flag on the
 * suggestion rather than being inferred here.
 */
export function shouldAutoSelect({ matches }) {
    if (!Array.isArray(matches)) return false;
    return matches.filter((match) => match?.exactMatch).length === 1;
}

/** Whether this variant carries the barcode the selected label type prints. */
export function isPrintable(variant, skuType) {
    return skuType === 'fnsku' ? Boolean(variant?.amazonSku) : Boolean(variant?.barcode);
}

/**
 * Whether the label count is fixed at zero and cannot be typed over.
 *
 * A confirmed zero or negative is locked: stock physically present with no recorded
 * on-hand is an inventory error to correct, not a label to print. When inventory could
 * not be loaded at all, nothing is locked — the lock applies to a confirmed zero, not
 * to an unknown.
 */
export function isLocked(variant, { inventoryKnown }) {
    if (!inventoryKnown) return false;
    return !Number.isFinite(variant?.onHand) || variant.onHand <= 0;
}

/** One label per unit on hand, zero for anything locked. */
export function initialLabelCounts({ variants, inventoryKnown }) {
    return (variants ?? []).reduce((counts, variant) => {
        const onHand = variant?.onHand;
        // Locked rows get 0, and so does an unknown count — which is every row when
        // inventory failed to load and the operator types the numbers themselves.
        counts[variant.sku] = isLocked(variant, { inventoryKnown }) || !Number.isFinite(onHand) || onHand < 1 ? 0 : onHand;
        return counts;
    }, {});
}

/** The footer's "N variants to print • M labels". */
export function printableTotals({ variants, counts, skuType, inventoryKnown }) {
    return (variants ?? []).reduce(
        (totals, variant) => {
            if (!isPrintable(variant, skuType)) return totals;
            if (isLocked(variant, { inventoryKnown })) return totals;

            const count = Number(counts?.[variant.sku]) || 0;
            if (count < 1) return totals;

            return { variantsToPrint: totals.variantsToPrint + 1, labels: totals.labels + count };
        },
        { variantsToPrint: 0, labels: 0 }
    );
}
